import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Grid
} from '@mui/material';
import {
  Close,
  LocationOn,
  Agriculture,
  Grass,
  LocalFlorist,
  Park,
  Terrain,
  Nature,
  Yard,
  CloudUpload,
  Description
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import LocationPicker from './LocationPicker';

// Custom hook to detect mobile screens
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

// Styled components matching CreateFieldForm
const StyledDialog = styled(Dialog)(({ theme, isMobile }) => ({
  '& .MuiDialog-paper': {
    borderRadius: isMobile ? '12px' : '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e7ff',
    maxWidth: isMobile ? '300px' : '800px',
    width: isMobile ? '300px' : '400px',
    margin: isMobile ? '8px' : '32px',
    background: 'linear-gradient(135deg, #f8fffe 0%, #f1f8e9 100%)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme, isMobile }) => ({
  background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
  color: 'white',
  fontWeight: 700,
  fontSize: isMobile ? '1.1rem' : '1.5rem',
  textAlign: 'center',
  padding: isMobile ? '16px' : '24px',
  borderRadius: isMobile ? '12px 12px 0 0' : '20px 20px 0 0',
}));

const StyledDialogContent = styled(DialogContent)(({ theme, isMobile }) => ({
  padding: isMobile ? '8px 16px 16px 16px !important' : '5px 32px 32px 32px !important',
  paddingTop: isMobile ? '8px !important' : '10px !important',
  background: 'linear-gradient(135deg, #f8fffe 0%, #f1f8e9 100%)',
  maxHeight: '80vh',
  overflowY: 'auto',
  marginLeft: isMobile ? '0 !important' : '30px !important',
}));

const StyledTextField = styled(TextField)(({ theme, isMobile }) => ({
  width: isMobile ? '268px !important' : '270px !important',
  '& .MuiOutlinedInput-root': {
    borderRadius: isMobile ? '8px' : '12px',
    backgroundColor: '#ffffff',
    border: '2px solid #e8f5e8',
    transition: 'all 0.3s ease',
    fontSize: isMobile ? '14px' : '16px',
    '&:hover': {
      borderColor: '#c8e6c9',
      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.1)',
    },
    '&.Mui-focused': {
      borderColor: '#4caf50',
      boxShadow: '0 4px 20px rgba(76, 175, 80, 0.2)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#4a5568',
    fontWeight: 500,
    fontSize: isMobile ? '14px' : '16px',
    '&.Mui-focused': {
      color: '#4caf50',
    },
  },
  '& .MuiFormHelperText-root': {
    fontSize: isMobile ? '11px' : '12px',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme, isMobile }) => ({
  width: isMobile ? '268px !important' : '270px !important',
  '& .MuiOutlinedInput-root': {
    borderRadius: isMobile ? '8px' : '12px',
    backgroundColor: '#ffffff',
    border: '2px solid #e8f5e8',
    minWidth: isMobile ? '100%' : '200px',
    transition: 'all 0.3s ease',
    fontSize: isMobile ? '14px' : '16px',
    '&:hover': {
      borderColor: '#c8e6c9',
      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.1)',
    },
    '&.Mui-focused': {
      borderColor: '#4caf50',
      boxShadow: '0 4px 20px rgba(76, 175, 80, 0.2)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#4a5568',
    fontWeight: 500,
    fontSize: isMobile ? '14px' : '16px',
    '&.Mui-focused': {
      color: '#4caf50',
    },
  },
  '& .MuiSelect-select': {
    width: isMobile ? '268px !important' : '270px !important',
    fontSize: isMobile ? '14px' : '16px',
  },
}));

const StyledButton = styled(Button)(({ theme, isMobile }) => ({
  borderRadius: isMobile ? '8px' : '12px',
  padding: isMobile ? '10px 20px' : '12px 32px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: isMobile ? '0.875rem' : '1rem',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  },
}));

const farmIcons = [
  { value: 'agriculture', label: 'Agriculture', icon: <Agriculture sx={{ color: '#8bc34a' }} /> },
  { value: 'grass', label: 'Grass Field', icon: <Grass sx={{ color: '#4caf50' }} /> },
  { value: 'eco', label: 'Eco Farm', icon: <Nature sx={{ color: '#2e7d32' }} /> },
  { value: 'flower', label: 'Flower Farm', icon: <LocalFlorist sx={{ color: '#e91e63' }} /> },
  { value: 'park', label: 'Park Farm', icon: <Park sx={{ color: '#388e3c' }} /> },
  { value: 'terrain', label: 'Terrain', icon: <Terrain sx={{ color: '#795548' }} /> },
  { value: 'nature', label: 'Nature Farm', icon: <Nature sx={{ color: '#689f38' }} /> },
  { value: 'yard', label: 'Yard', icon: <Yard sx={{ color: '#558b2f' }} /> }
];

// Map API farm (snake_case) to form initial values
const apiFarmToFormInitial = (farm) => {
  if (!farm) return null;
  let coords = { lat: null, lng: null };
  if (farm.coordinates) {
    if (Array.isArray(farm.coordinates)) {
      coords = { lng: farm.coordinates[0], lat: farm.coordinates[1] };
    } else if (farm.coordinates.lat != null && farm.coordinates.lng != null) {
      coords = { lat: farm.coordinates.lat, lng: farm.coordinates.lng };
    }
  }
  return {
    farmName: farm.farm_name || farm.name || '',
    farmIcon: farm.farm_icon || farm.farmIcon || '',
    location: farm.location || '',
    coordinates: coords,
    webcamUrl: farm.webcam_url || farm.webcamUrl || '',
    description: farm.description || '',
    status: farm.status || 'Active',
    cropType: farm.crop_type || farm.cropType || '',
    irrigationType: farm.irrigation_type || farm.irrigationType || '',
    soilType: farm.soil_type || farm.soilType || '',
    areaValue: farm.area_value || farm.areaValue || '',
    areaUnit: farm.area_unit || farm.areaUnit || 'acres',
    monthlyRevenue: farm.monthly_revenue || farm.monthlyRevenue || '',
    progress: farm.progress || 0,
    plantingDate: farm.planting_date || farm.plantingDate ? new Date(farm.planting_date || farm.plantingDate).toISOString().split('T')[0] : '',
    harvestDate: farm.harvest_date || farm.harvestDate ? new Date(farm.harvest_date || farm.harvestDate).toISOString().split('T')[0] : '',
    licenseFile: null
  };
};

const AddFarmForm = ({ open, onClose, onSubmit, editMode = false, initialData = null }) => {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    farmName: '',
    farmIcon: '',
    location: '',
    coordinates: { lat: null, lng: null },
    webcamUrl: '',
    description: '',
    status: 'Active',
    cropType: '',
    irrigationType: '',
    soilType: '',
    areaValue: '',
    areaUnit: 'acres',
    monthlyRevenue: '',
    progress: 0,
    plantingDate: '',
    harvestDate: '',
    licenseFile: null
  });
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [errors, setErrors] = useState({});

  // When opening in edit mode, prefill from initialData
  useEffect(() => {
    if (open && editMode && initialData) {
      const initial = apiFarmToFormInitial(initialData);
      if (initial) setFormData(initial);
    }
    if (open && !editMode) {
      setFormData({
        farmName: '',
        farmIcon: '',
        location: '',
        coordinates: { lat: null, lng: null },
        webcamUrl: '',
        description: '',
        status: 'Active',
        cropType: '',
        irrigationType: '',
        soilType: '',
        areaValue: '',
        areaUnit: 'acres',
        monthlyRevenue: '',
        progress: 0,
        plantingDate: '',
        harvestDate: '',
        licenseFile: null
      });
    }
  }, [open, editMode, initialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleLocationSelect = (locationData) => {
    // Convert array coordinates [lng, lat] to object format
    const coordinates = Array.isArray(locationData.coordinates)
      ? { lng: locationData.coordinates[0], lat: locationData.coordinates[1] }
      : locationData.coordinates;

    setFormData(prev => ({
      ...prev,
      location: locationData.address,
      coordinates: coordinates
    }));
    setLocationPickerOpen(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.farmName.trim()) {
      newErrors.farmName = 'Farm name is required';
    }

    if (!formData.farmIcon || formData.farmIcon.trim() === '') {
      newErrors.farmIcon = 'Please select a farm icon';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (formData.webcamUrl.trim()) {
      try {
        new URL(formData.webcamUrl);
      } catch {
        newErrors.webcamUrl = 'Please enter a valid URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const farmData = {
        ...formData,
        name: formData.farmName,
      };
      if (editMode && initialData && initialData.id) {
        farmData.id = initialData.id;
      } else {
        farmData.id = Date.now();
        farmData.created_at = new Date().toISOString();
      }
      onSubmit(farmData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      farmName: '',
      farmIcon: '',
      location: '',
      coordinates: { lat: null, lng: null },
      webcamUrl: '',
      description: '',
      status: 'Active',
      cropType: '',
      irrigationType: '',
      soilType: '',
      areaValue: '',
      areaUnit: 'acres',
      monthlyRevenue: '',
      progress: 0,
      plantingDate: '',
      harvestDate: '',
      licenseFile: null
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={handleClose}
        maxWidth={isMobile ? false : "md"}
        fullWidth={!isMobile}
        isMobile={isMobile}
      >
        <StyledDialogTitle isMobile={isMobile}>
          {editMode ? 'Edit Farm' : 'Add New Farm'}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: isMobile ? 8 : 16,
              top: isMobile ? 8 : 16,
              color: 'white',
              '& .MuiSvgIcon-root': {
                fontSize: isMobile ? '20px' : '24px'
              }
            }}
          >
            <Close />
          </IconButton>
        </StyledDialogTitle>

        <StyledDialogContent isMobile={isMobile}>
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Farm Name"
                value={formData.farmName}
                onChange={(e) => handleInputChange('farmName', e.target.value)}
                error={!!errors.farmName}
                helperText={errors.farmName}
                variant="outlined"
                placeholder="Enter farm name"
                isMobile={isMobile}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledFormControl fullWidth error={!!errors.farmIcon} isMobile={isMobile}>
                <InputLabel>Select Farm Icon</InputLabel>
                <Select
                  value={formData.farmIcon}
                  onChange={(e) => handleInputChange('farmIcon', e.target.value)}
                  renderValue={(selected) => {
                    const selectedItem = farmIcons.find(icon => icon.value === selected);
                    return selectedItem ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {selectedItem.icon}
                        <Typography sx={{ ml: 1 }}>{selectedItem.label}</Typography>
                      </Box>
                    ) : null;
                  }}
                >
                  {farmIcons.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {icon.icon}
                        <Typography sx={{ ml: 1 }}>{icon.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.farmIcon && <Typography variant="caption" color="error">{errors.farmIcon}</Typography>}
              </StyledFormControl>
            </Grid>

            {/* Farm Properties */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Crop Type"
                value={formData.cropType}
                onChange={(e) => handleInputChange('cropType', e.target.value)}
                placeholder="e.g. Wheat, Mixed, Rice"
                isMobile={isMobile}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Farm Status"
                select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                isMobile={isMobile}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Growing">Growing</MenuItem>
                <MenuItem value="Harvesting">Harvesting</MenuItem>
                <MenuItem value="Planning">Planning</MenuItem>
              </StyledTextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Irrigation Type"
                value={formData.irrigationType}
                onChange={(e) => handleInputChange('irrigationType', e.target.value)}
                placeholder="e.g. Drip, Sprinkler"
                isMobile={isMobile}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Soil Type"
                value={formData.soilType}
                onChange={(e) => handleInputChange('soilType', e.target.value)}
                placeholder="e.g. Loamy, Clayey"
                isMobile={isMobile}
              />
            </Grid>

            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Area Value"
                type="number"
                value={formData.areaValue}
                onChange={(e) => handleInputChange('areaValue', e.target.value)}
                placeholder="e.g. 25"
                isMobile={isMobile}
              />
            </Grid>

            <Grid item xs={12}>
              <StyledFormControl fullWidth isMobile={isMobile}>
                <InputLabel>Area Unit</InputLabel>
                <Select
                  value={formData.areaUnit}
                  onChange={(e) => handleInputChange('areaUnit', e.target.value)}
                  label="Area Unit"
                >
                  <MenuItem value="acres">Acres</MenuItem>
                  <MenuItem value="hectares">Hectares</MenuItem>
                  <MenuItem value="sqm">m²</MenuItem>
                </Select>
              </StyledFormControl>
            </Grid>

            {/* Dates */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Planting Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.plantingDate}
                onChange={(e) => handleInputChange('plantingDate', e.target.value)}
                isMobile={isMobile}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Harvest Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.harvestDate}
                onChange={(e) => handleInputChange('harvestDate', e.target.value)}
                isMobile={isMobile}
              />
            </Grid>

            {/* Location & Webcam */}
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Location"
                value={formData.location}
                error={!!errors.location}
                helperText={errors.location}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setLocationPickerOpen(true)}>
                        <LocationOn sx={{ color: '#4caf50' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                isMobile={isMobile}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Webcam URL (Optional)"
                value={formData.webcamUrl}
                onChange={(e) => handleInputChange('webcamUrl', e.target.value)}
                error={!!errors.webcamUrl}
                helperText={errors.webcamUrl}
                isMobile={isMobile}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                error={!!errors.description}
                helperText={errors.description}
                isMobile={isMobile}
              />
            </Grid>

            {/* License Upload */}
            <Grid item xs={12}>
              <Box sx={{ border: '2px dashed #c8e6c9', p: 2, borderRadius: '12px', textAlign: 'center' }}>
                <StyledButton
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  sx={{ color: '#4caf50', borderColor: '#4caf50' }}
                >
                  Upload License
                  <input
                    type="file"
                    hidden
                    onChange={(e) => handleInputChange('licenseFile', e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                </StyledButton>
                {formData.licenseFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>{formData.licenseFile.name}</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </StyledDialogContent>

        <DialogActions sx={{ p: 3, background: 'linear-gradient(135deg, #f8fffe 0%, #f1f8e9 100%)' }}>
          <StyledButton onClick={handleClose} variant="outlined" sx={{ color: '#666' }}>
            Cancel
          </StyledButton>
          <StyledButton onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#4caf50' }}>
            {editMode ? 'Update Farm' : 'Save Farm'}
          </StyledButton>
        </DialogActions>
      </StyledDialog>

      <LocationPicker
        open={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={formData.coordinates.lat ? formData.coordinates : null}
      />
    </>
  );
};

export default AddFarmForm;